import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAuthSession } from '@aws-amplify/auth';
import { Spinner } from 'flowbite-react';
import * as sched from 'sched';
import { useAuth } from '../hooks/useAuth';
import { useWebSocket, WebSocketCallbacks, CommandBatch } from '../hooks/useWebSocket';
import { FiCopy, FiChevronDown } from 'react-icons/fi';
import ClearLocalCacheButton from '../components/common/ClearLocalCacheButton';
import { useAsync } from 'react-async-hook';
import { buildWorkspaceUrl, WorkspaceUrlTarget } from '../routes/workspaceUrl';

/** Callback for receiving conversation messages via WebSocket. */
export type ConversationMessageListener = (message: unknown) => void;
/** Callback for receiving conversation status changes via WebSocket. */
export type ConversationStatusListener = (conversationId: string, status: { type: string }) => void;

// Define the context type
interface WorkspaceContextType {
  safeData: sched.SafeData;
  /** Canonical workspace UUID. Use this for ALL API, WASM, and storage calls. */
  workspaceId: string;
  /**
   * URL-friendly workspace identifier — the user-chosen slug if claimed,
   * otherwise the UUID. Internal-only: prefer `buildUrl` for any URL
   * construction so the templating stays in one place.
   */
  workspaceSlug: string;
  /**
   * Build a workspace-scoped URL for the current workspace. Use this
   * everywhere URLs are constructed in the app — never inline-template
   * `/workspaces/${...}` strings. See `routes/workspaceUrl.ts` for the
   * exhaustive set of targets.
   */
  buildUrl: (target: WorkspaceUrlTarget) => string;
  getUserProfile: (userId: string) => Promise<sched.UserProfile | undefined>;
  /** Whether the WebSocket connection is established. */
  wsConnected: boolean;
  /** Submit CQRS commands via WebSocket. Returns correlation_id. */
  submitCommands: (commands: unknown[]) => string;
  /** The currently in-flight command batch, if any. */
  inFlightBatch: CommandBatch | null;
  /** Dismiss a failed batch to unblock the queue. */
  dismissFailedBatch: (correlationId: string) => void;
  /** Subscribe to a conversation's WebSocket events. Returns unsubscribe function. */
  subscribeConversation: (
    conversationId: string,
    onMessage: ConversationMessageListener,
    onStatus: ConversationStatusListener,
  ) => () => void;
}

// Create the context with default values
export const WorkspaceContext = createContext<WorkspaceContextType>(undefined!);

// Hook to use the workspace context
export const useWorkspace = () => useContext(WorkspaceContext);

// ── Workspace error rendering ─────────────────────────────────────
// Branch on the structured `WorkspaceLoadError` (see
// lib/src/wasm/workspace_error.rs) so we can present each failure mode
// purposefully rather than parsing strings.
const WorkspaceErrorDetails: React.FC<{ error: sched.WorkspaceLoadError }> = ({ error }) => {
  switch (error.type) {
    case 'Decode':
      return (
        <div className="mb-6 space-y-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            The server's response could not be parsed. This usually means the
            database schema has changed since these events were written.
          </p>
          {error.path && (
            <ErrorField label="Failed at" value={error.path} breakAll />
          )}
          <ErrorField label="Reason" value={error.message} />
          <ErrorBlock
            label="Response body"
            value={error.response_body}
            collapsible
          />
        </div>
      );
    case 'Http':
      return (
        <div className="mb-6 space-y-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            The server returned an error status.
          </p>
          <ErrorField label="Status" value={String(error.status)} />
          {error.body && <ErrorField label="Body" value={error.body} />}
        </div>
      );
    case 'Network':
      return (
        <div className="mb-6 space-y-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Couldn't reach the server.
          </p>
          <ErrorField label="Detail" value={error.message} />
        </div>
      );
    case 'Storage':
      return (
        <div className="mb-6 space-y-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Local browser storage failed.
          </p>
          <ErrorField label="Detail" value={error.message} />
        </div>
      );
    case 'Other':
      return (
        <div className="mb-6">
          <ErrorField label="Detail" value={error.message} />
        </div>
      );
  }
};

const ErrorField: React.FC<{ label: string; value: string; breakAll?: boolean }> = ({
  label,
  value,
  breakAll,
}) => (
  <div>
    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
      {label}
    </div>
    <pre
      className={`text-xs font-mono bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-2 overflow-x-auto whitespace-pre-wrap text-gray-800 dark:text-gray-200 ${
        breakAll ? 'break-all' : ''
      }`}
    >
      {value}
    </pre>
  </div>
);

const ErrorBlock: React.FC<{ label: string; value: string; collapsible?: boolean }> = ({
  label,
  value,
  collapsible,
}) => {
  if (!value) return null;
  if (collapsible) {
    return (
      <details className="group">
        <summary className="cursor-pointer text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 hover:text-gray-700 dark:hover:text-gray-200">
          {label} (click to expand)
        </summary>
        <pre className="mt-1 text-xs font-mono bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-2 max-h-64 overflow-auto whitespace-pre-wrap text-gray-800 dark:text-gray-200">
          {value}
        </pre>
      </details>
    );
  }
  return <ErrorField label={label} value={value} />;
};

interface WorkspaceProviderProps {
  children: ReactNode;
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children }) => {
  // The URL path segment is opaque — could be a UUID or a user-chosen slug.
  // We resolve it to a canonical workspace (UUID + slug) via the get-workspace
  // API before doing any other work; afterwards, `workspaceId` is the UUID
  // and `workspaceSlug` is the slug (or UUID fallback).
  const { workspaceId: urlWorkspaceParam } = useParams<{ workspaceId: string }>();
  const { client, user, token } = useAuth();
  const [resolvedWorkspace, setResolvedWorkspace] = useState<sched.Workspace | null>(null);
  const [safeData, setSafeData] = useState<sched.SafeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<sched.WorkspaceLoadError | null>(null);
  const [serializedDatabase, setSerializedDatabase] = useState<string | null>(null);
  const [missingUserProfileIds, setMissingUserProfileIds] = useState<string[]>([]);

  // Canonical UUID — undefined until resolved. Provider blocks rendering
  // (via the `loading` spinner) until this exists, so all downstream code
  // can treat it as a guaranteed string.
  const workspaceId: string | undefined = resolvedWorkspace?.id;
  const workspaceSlug: string | undefined = resolvedWorkspace
    ? (resolvedWorkspace.slug ?? resolvedWorkspace.id)
    : undefined;

  // Keep safeData in a ref so WebSocket callbacks can access the latest value
  // without causing the callbacks (and thus the WS connection) to be recreated.
  const safeDataRef = React.useRef<sched.SafeData | null>(null);
  safeDataRef.current = safeData;

  // ── Conversation listener registry ────────────────────────────
  // Consumers (useConversation) register listeners for their active conversation.
  // The WebSocket callbacks dispatch to all registered listeners.
  const conversationListenersRef = React.useRef<
    Map<string, { onMessage: ConversationMessageListener; onStatus: ConversationStatusListener }>
  >(new Map());

  const subscribeConversation = useCallback(
    (
      conversationId: string,
      onMessage: ConversationMessageListener,
      onStatus: ConversationStatusListener,
    ) => {
      conversationListenersRef.current.set(conversationId, { onMessage, onStatus });
      // Subscribe to the conversation entity over WebSocket
      wsSubscribeRef.current?.(conversationId);
      return () => {
        conversationListenersRef.current.delete(conversationId);
        wsUnsubscribeRef.current?.(conversationId);
      };
    },
    [],
  );

  // Refs for WS subscribe/unsubscribe so subscribeConversation doesn't depend on them
  const wsSubscribeRef = React.useRef<((entityId: string, afterEventId?: number) => void) | null>(null);
  const wsUnsubscribeRef = React.useRef<((entityId: string) => void) | null>(null);

  // ── Auto-save: track correlation_id → pending_change_ids ──────
  // When a batch is confirmed, we clear the corresponding pending changes from WASM.
  const batchPendingIdsRef = React.useRef<Map<string, string[]>>(new Map());
  // All pending change IDs that are currently claimed by in-flight or queued batches.
  // These are excluded from subsequent flushes to prevent double-sending.
  const claimedIdsRef = React.useRef<Set<string>>(new Set());

  // ── WebSocket callbacks (stable refs — don't depend on safeData) ──
  const wsCallbacks: WebSocketCallbacks = React.useMemo(
    () => ({
      onCqrsEvents: async (_workspaceId, events, correlationId) => {
        const sd = safeDataRef.current;
        if (!sd) return;
        try {
          const eventsJson = JSON.stringify(events);
          await sched.DataManager.apply_remote_events_from_json(sd, eventsJson);
        } catch (err) {
          console.error('[ws] failed to apply CQRS events:', err);
        }

        // If this confirms a batch we sent, clear those pending changes
        if (correlationId) {
          const pendingIds = batchPendingIdsRef.current.get(correlationId);
          if (pendingIds && sd) {
            try {
              await sched.DataManager.remove_confirmed_pending_changes(
                sd,
                JSON.stringify(pendingIds),
              );
            } catch (err) {
              console.error('[ws] failed to clear confirmed pending changes:', err);
            }
            // Release claimed IDs
            for (const id of pendingIds) {
              claimedIdsRef.current.delete(id);
            }
            batchPendingIdsRef.current.delete(correlationId);
          }
        }
      },
      onConversationMessage: (message: unknown) => {
        // Dispatch to the listener for this conversation
        const convId = (message as any)?.conversation_id;
        if (convId) {
          conversationListenersRef.current.get(convId)?.onMessage(message);
        }
      },
      onConversationStatusChange: (conversationId: string, status: { type: string }) => {
        conversationListenersRef.current.get(conversationId)?.onStatus(conversationId, status);
      },
      onCommandError: (correlationId, error) => {
        console.error(`[ws] command error (${correlationId}):`, error);
        // Release claimed IDs so the pending changes remain and can be
        // retried or undone by the user.
        const pendingIds = batchPendingIdsRef.current.get(correlationId);
        if (pendingIds) {
          for (const id of pendingIds) {
            claimedIdsRef.current.delete(id);
          }
          batchPendingIdsRef.current.delete(correlationId);
        }
      },
    }),
    [],
  );

  const {
    connected: wsConnected,
    subscribe: wsSubscribe,
    unsubscribe: wsUnsubscribe,
    submitCommands: wsSubmitCommands,
    inFlightBatch,
    dismissFailedBatch,
  } = useWebSocket(wsCallbacks);

  // Update refs so subscribeConversation can use them without dep cycles
  wsSubscribeRef.current = wsSubscribe;
  wsUnsubscribeRef.current = wsUnsubscribe;

  // Wrap submitCommands to inject workspaceId
  const submitCommands = useCallback(
    (commands: unknown[]) => {
      if (!workspaceId) throw new Error('no workspaceId');
      return wsSubmitCommands(workspaceId, commands);
    },
    [workspaceId, wsSubmitCommands],
  );

  // URL builder bound to the current workspace's slug. Components MUST use
  // this (or the standalone `buildWorkspaceUrl`) for any workspace-scoped
  // URL construction — no inline `/workspaces/${...}` templating.
  const buildUrl = useCallback(
    (target: WorkspaceUrlTarget) => {
      if (!workspaceSlug) throw new Error('buildUrl called before workspace resolved');
      return buildWorkspaceUrl(workspaceSlug, target);
    },
    [workspaceSlug],
  );

  // ── Auto-save: flush pending changes to server via WebSocket ────
  // Subscribes to PendingChanges notifications from WASM. When new pending
  // changes appear, sends them as a command batch if the channel is clear.
  const flushingRef = React.useRef(false);

  const flushPendingChanges = useCallback(async () => {
    const sd = safeDataRef.current;
    if (!sd || !workspaceId) {
      console.log('[auto-save] skip: no safeData or workspaceId');
      return;
    }
    if (!wsConnected) {
      console.log('[auto-save] skip: WebSocket not connected');
      return;
    }
    if (flushingRef.current) {
      console.log('[auto-save] skip: already flushing');
      return;
    }
    if (inFlightBatch) {
      console.log('[auto-save] skip: batch in flight');
      return;
    }

    flushingRef.current = true;
    try {
      // Get commands that aren't already claimed by an in-flight/queued batch
      const excludeJson = JSON.stringify([...claimedIdsRef.current]);
      const payloadJson = await sched.DataManager.get_unsent_pending_commands_as_json(
        sd,
        excludeJson,
      );
      if (!payloadJson) return; // Nothing new to send

      const payload = JSON.parse(payloadJson);
      const commands: unknown[] = payload.commands;
      const pendingChangeIds: string[] = payload.pending_change_ids;

      if (commands.length === 0) return;
      console.log(`[auto-save] flushing ${commands.length} commands, ids=${pendingChangeIds.join(',').substring(0, 60)}...`);

      // Claim these IDs so they aren't re-sent
      for (const id of pendingChangeIds) {
        claimedIdsRef.current.add(id);
      }

      const correlationId = wsSubmitCommands(workspaceId, commands);
      batchPendingIdsRef.current.set(correlationId, pendingChangeIds);
    } catch (err) {
      console.error('[auto-save] flush failed:', err);
    } finally {
      flushingRef.current = false;
    }
  }, [workspaceId, wsConnected, inFlightBatch, wsSubmitCommands]);

  // Ref to always call the latest flushPendingChanges without re-subscribing
  const flushRef = React.useRef(flushPendingChanges);
  flushRef.current = flushPendingChanges;

  // Subscribe to PendingChanges ONCE when safeData is available.
  // The callback uses flushRef so it always calls the current version.
  useEffect(() => {
    if (!safeData) return;

    console.log('[auto-save] subscribing to pending changes');

    const callback = (newPendingChanges: unknown) => {
      const count = Array.isArray(newPendingChanges) ? newPendingChanges.length : '?';
      console.log(`[auto-save] pending changes updated (${count} items)`);
      setTimeout(() => flushRef.current(), 0);
    };
    const jsCallback = callback as unknown as Function;

    sched.DataManager.select_pending_changes(safeData, jsCallback)
      .then(() => console.log('[auto-save] subscribed successfully'))
      .catch((err: any) => console.error('[auto-save] subscribe failed:', err));

    return () => {
      if ((safeData as any).__wbg_ptr !== 0) {
        sched.DataManager.unsubscribe(safeData, jsCallback)
          .catch((err: any) => console.error('Error unsubscribing:', err));
      }
    };
  }, [safeData]); // Only depends on safeData — stable subscription

  // Also flush when connection establishes or a batch completes
  useEffect(() => {
    if (!inFlightBatch && wsConnected) {
      flushPendingChanges();
    }
  }, [inFlightBatch, wsConnected, flushPendingChanges]);

  const listUserProfilesResponse = useAsync(async () => {
    if (!client || !token || !workspaceId) return [];

    return (await sched.WrappedApiClient.list_workspace_user_profiles(client, token, workspaceId)).user_profiles;
  }, [
    token, workspaceId
  ]);

  useEffect(() => {
    let cancelled = false;
    const loadWorkspace = async () => {
      if (!urlWorkspaceParam || !user || !client) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get the authentication token
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();

        if (!token) {
          throw new Error('No authentication token available');
        }

        // Resolve the URL param (UUID or slug) to a canonical workspace.
        // Backend shape-detects and falls back to slug-index lookup. The
        // returned Workspace carries the canonical UUID we use everywhere
        // downstream. We deliberately do NOT mutate the URL — whatever the
        // user typed stays. New navigations (from links etc.) go to the slug
        // form via the URL helpers.
        const workspace = await sched.WrappedApiClient.get_workspace(client, token, urlWorkspaceParam);
        if (cancelled) return;
        setResolvedWorkspace(workspace);

        // Load the data manager using the canonical UUID — never the slug.
        const manager = await sched.SafeData.load(client, workspace.id, token);
        if (cancelled) return;
        setSafeData(manager);

        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading workspace:', err);
        // `sched.SafeData.load` rejects with a typed `WorkspaceLoadError`
        // (see lib/src/wasm/workspace_error.rs). Plain JS Errors / strings
        // can also reach here from non-Rust failure paths (including the
        // resolve step above).
        if (err && typeof err === 'object' && 'type' in err) {
          setError(err as sched.WorkspaceLoadError);
        } else if (err instanceof Error) {
          setError({ type: 'Other', message: err.message });
        } else {
          setError({ type: 'Other', message: 'Failed to load workspace' });
        }

        // Best-effort cache load for the debug UI — only attempt this if we
        // managed to resolve a UUID first; storage is keyed by UUID.
        const cacheKey = resolvedWorkspace?.id;
        if (cacheKey) {
          const serializedDatabase = await sched.DataManager.load_database_from_storage_but_do_not_deserialize_yet(cacheKey);
          if (!cancelled) setSerializedDatabase(serializedDatabase);
        }

        setLoading(false);
      }
    };

    loadWorkspace();
    return () => { cancelled = true; };
    // Deliberately does not re-run on token change
  }, [urlWorkspaceParam, user, client]);

  // Subscribe to workspace events via WebSocket once data is loaded
  useEffect(() => {
    if (!safeData || !workspaceId || !wsConnected) return;

    (async () => {
      const lastEventId = await sched.DataManager.get_last_remote_event_id(safeData);
      wsSubscribe(workspaceId, lastEventId ?? undefined);
    })();
  }, [safeData, workspaceId, wsConnected, wsSubscribe]);

  // Return the cached user profile, if available. If not, refresh the query
  const getUserProfile = useCallback(async (userId: string) => {
    const profile = listUserProfilesResponse.result?.find((profile) => profile.id === userId);
    if (!profile && !missingUserProfileIds.includes(userId)) {
      setMissingUserProfileIds([...missingUserProfileIds, userId]);
      await listUserProfilesResponse.execute();
      return listUserProfilesResponse.result?.find((profile) => profile.id === userId);
    }
    return profile;
  }, [listUserProfilesResponse.result, missingUserProfileIds]);

  // Show loading spinner while data is being fetched
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Spinner size="xl" aria-label="Loading workspace" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading workspace...</p>
        </div>
      </div>
    );
  }

  // Show error message if loading failed
  if (error) {
    const copyError = () => {
      navigator.clipboard.writeText(JSON.stringify(error, null, 2));
    };

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-2xl p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">Error Loading Workspace</h2>

          <WorkspaceErrorDetails error={error} />

          <div className="flex justify-center gap-4 flex-wrap">
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={copyError}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
            >
              <FiCopy className="w-4 h-4" />
              Copy error
            </button>
            {workspaceId && <ClearLocalCacheButton workspaceId={workspaceId} />}
          </div>
          {serializedDatabase && (
            <div className="mt-4">
              <h4 className="text-sm font-medium mb-2">Database Debug Information</h4>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
                <details className="group">
                  <summary className="flex items-center justify-between p-4 cursor-pointer bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg">
                    <span className="font-medium">Database Debug View</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          if (serializedDatabase) {
                            navigator.clipboard.writeText(JSON.stringify(serializedDatabase, null, 2));
                            // Optional: Add a visual feedback like a tooltip or temporary text
                          }
                        }}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        title="Copy to clipboard"
                      >
                        <FiCopy className="w-5 h-5" />
                      </button>
                      <span className="transition group-open:rotate-180">
                        <FiChevronDown className="w-5 h-5" />
                      </span>
                    </div>
                  </summary>
                  <div className="p-4 bg-white dark:bg-gray-900">
                    <div className="max-h-96 overflow-auto">
                      <pre className="text-xs whitespace-pre-wrap bg-gray-100 dark:bg-gray-800 p-4 rounded">
                        {JSON.stringify(serializedDatabase, null, 2)}
                      </pre>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // If no workspace ID is provided, show a message
  if (!workspaceId || !workspaceSlug || !safeData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No Workspace Selected</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">Please select a workspace to continue.</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <WorkspaceContext.Provider value={{
      safeData,
      workspaceId,
      workspaceSlug,
      buildUrl,
      getUserProfile,
      wsConnected,
      submitCommands,
      inFlightBatch,
      dismissFailedBatch,
      subscribeConversation,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
};
